import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Card,
  CardContent,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Divider,
  Autocomplete
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CompareIcon from '@mui/icons-material/Compare';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const IndicatorTesting = () => {
  const [supportedIndicators, setSupportedIndicators] = useState({});
  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [symbolsLoading, setSymbolsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  
  // Form state
  const [selectedSymbol, setSelectedSymbol] = useState('RELIANCE');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedIndicator, setSelectedIndicator] = useState('');
  const [parameters, setParameters] = useState({});
  const [timeframe, setTimeframe] = useState('1D');
  const [periodDays, setPeriodDays] = useState(100);
  
  // Comparison state
  const [comparisonIndicators, setComparisonIndicators] = useState([]);
  const [comparisonResults, setComparisonResults] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const loadSupportedIndicators = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/indicator-testing/supported-indicators`);
      const data = await response.json();
      setSupportedIndicators(data.indicators);
      
      // Set default selection
      const firstCategory = Object.keys(data.indicators)[0];
      if (firstCategory) {
        setSelectedCategory(firstCategory);
        const firstIndicator = Object.keys(data.indicators[firstCategory])[0];
        if (firstIndicator) {
          setSelectedIndicator(firstIndicator);
          initializeParameters(data.indicators[firstCategory][firstIndicator]);
        }
      }
    } catch (err) {
      setError('Failed to load supported indicators');
      console.error(err);
    }
  };

  const loadAvailableSymbols = async () => {
    setSymbolsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/indicator-testing/symbols`);
      const data = await response.json();
      console.log('Loaded symbols:', data.symbols?.length || 0, 'symbols');
      
      // Sort symbols with popular ones first
      const popularSymbols = ['RELIANCE', 'TCS', 'INFY', 'HDFCBANK', 'ICICIBANK', 'ITC', 'SBIN', 'LT', 'BHARTIARTL', 'KOTAKBANK'];
      const symbols = data.symbols || [];
      
      const sortedSymbols = [
        ...symbols.filter(symbol => popularSymbols.includes(symbol)).sort(),
        ...symbols.filter(symbol => !popularSymbols.includes(symbol)).sort()
      ];
      
      setAvailableSymbols(sortedSymbols);
      
      // Set default symbol if none selected and symbols are available
      if (!selectedSymbol && sortedSymbols.length > 0) {
        // Use first popular symbol that exists, otherwise first available
        const foundPopular = popularSymbols.find(sym => symbols.includes(sym));
        setSelectedSymbol(foundPopular || sortedSymbols[0]);
      }
    } catch (err) {
      console.error('Failed to load symbols:', err);
      setError('Failed to load available symbols');
    } finally {
      setSymbolsLoading(false);
    }
  };

  // Load supported indicators and symbols on mount
  useEffect(() => {
    loadSupportedIndicators();
    loadAvailableSymbols();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeParameters = (indicatorInfo) => {
    const params = {};
    if (indicatorInfo.parameters) {
      indicatorInfo.parameters.forEach(param => {
        params[param.name] = param.default;
      });
    }
    setParameters(params);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedIndicator('');
    setParameters({});
    
    if (supportedIndicators[category]) {
      const firstIndicator = Object.keys(supportedIndicators[category])[0];
      if (firstIndicator) {
        setSelectedIndicator(firstIndicator);
        initializeParameters(supportedIndicators[category][firstIndicator]);
      }
    }
  };

  const handleIndicatorChange = (indicator) => {
    setSelectedIndicator(indicator);
    if (supportedIndicators[selectedCategory] && supportedIndicators[selectedCategory][indicator]) {
      initializeParameters(supportedIndicators[selectedCategory][indicator]);
    }
  };

  const handleParameterChange = (paramName, value) => {
    setParameters(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  const testSingleIndicator = async () => {
    if (!selectedIndicator || !selectedSymbol) {
      setError('Please select a symbol and indicator');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const paramValues = Object.values(parameters);
      
      const response = await fetch(`${API_BASE_URL}/api/v1/indicator-testing/test-indicator`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol: selectedSymbol,
          indicator: selectedIndicator,
          parameters: paramValues,
          timeframe: timeframe,
          period_days: periodDays
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResults(data);
        setComparisonResults(null);
      } else {
        setError(data.error || 'Failed to calculate indicator');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const addToComparison = () => {
    if (!selectedIndicator) return;
    
    const paramValues = Object.values(parameters);
    const newIndicator = {
      indicator: selectedIndicator,
      parameters: paramValues,
      displayName: `${selectedIndicator}(${paramValues.join(',')})`
    };
    
    setComparisonIndicators(prev => [...prev, newIndicator]);
  };

  const runComparison = async () => {
    if (comparisonIndicators.length === 0) {
      setError('Add indicators to comparison first');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/indicator-testing/test-multiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol: selectedSymbol,
          indicators: comparisonIndicators.map(ind => ({
            indicator: ind.indicator,
            parameters: ind.parameters
          })),
          timeframe: timeframe,
          period_days: periodDays
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setComparisonResults(data);
        setResults(null);
      } else {
        setError(data.error || 'Failed to run comparison');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const removeFromComparison = (index) => {
    setComparisonIndicators(prev => prev.filter((_, i) => i !== index));
  };

  const prepareChartData = () => {
    if (results) {
      return results.dates.map((date, index) => ({
        date: date,
        value: results.historical_values[index],
        close: results.data.ohlcv?.close[index]
      })).filter(item => item.value !== null);
    }
    
    if (comparisonResults) {
      const chartData = comparisonResults.dates.map((date, index) => ({
        date: date,
        close: null // We'll need OHLCV data for this
      }));
      
      Object.entries(comparisonResults.results).forEach(([key, result]) => {
        if (result.success) {
          result.values.forEach((value, index) => {
            if (chartData[index]) {
              chartData[index][key] = value;
            }
          });
        }
      });
      
      return chartData;
    }
    
    return [];
  };

  const renderParameterInputs = () => {
    if (!supportedIndicators[selectedCategory] || !supportedIndicators[selectedCategory][selectedIndicator]) {
      return null;
    }

    const indicatorInfo = supportedIndicators[selectedCategory][selectedIndicator];
    
    if (!indicatorInfo.parameters || indicatorInfo.parameters.length === 0) {
      return <Typography variant="body2" color="textSecondary">No parameters required</Typography>;
    }

    return indicatorInfo.parameters.map(param => (
      <TextField
        key={param.name}
        label={param.name}
        type={param.type === 'int' ? 'number' : 'text'}
        value={parameters[param.name] || ''}
        onChange={(e) => handleParameterChange(param.name, 
          param.type === 'int' ? parseInt(e.target.value) || 0 : 
          param.type === 'float' ? parseFloat(e.target.value) || 0 : 
          e.target.value
        )}
        size="small"
        fullWidth
        margin="dense"
        inputProps={param.type === 'int' ? { 
          min: param.min || 1, 
          max: param.max || 100, 
          step: 1 
        } : param.type === 'float' ? {
          min: param.min || 0.1,
          max: param.max || 10,
          step: 0.1
        } : {}}
      />
    ));
  };

  const renderResults = () => {
    if (!results && !comparisonResults) return null;

    const chartData = prepareChartData();
    
    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Results for {selectedSymbol} ({timeframe})
        </Typography>
        
        {results && (
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Latest Value</Typography>
                    <Typography variant="h6">
                      {results.latest_value !== null ? results.latest_value.toFixed(4) : 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Data Points</Typography>
                    <Typography variant="h6">{results.data_points}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Calc Time</Typography>
                    <Typography variant="h6">{results.calculation_time_ms.toFixed(2)}ms</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Parameters</Typography>
                    <Typography variant="body2">
                      [{results.parameters.join(', ')}]
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {comparisonResults && (
          <Box sx={{ mb: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Indicator</TableCell>
                    <TableCell>Latest Value</TableCell>
                    <TableCell>Calc Time (ms)</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(comparisonResults.results).map(([key, result]) => (
                    <TableRow key={key}>
                      <TableCell>{key}</TableCell>
                      <TableCell>
                        {result.latest_value !== null ? result.latest_value.toFixed(4) : 'N/A'}
                      </TableCell>
                      <TableCell>{result.calculation_time_ms?.toFixed(2) || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={result.success ? 'Success' : 'Error'} 
                          color={result.success ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {chartData.length > 0 && (
          <Box sx={{ height: 300, mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Historical Values</Typography>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip />
                {results && (
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#2196f3" 
                    strokeWidth={2}
                    dot={false}
                  />
                )}
                {comparisonResults && Object.keys(comparisonResults.results).map((key, index) => (
                  <Line 
                    key={key}
                    type="monotone" 
                    dataKey={key} 
                    stroke={`hsl(${index * 60}, 70%, 50%)`}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Typography variant="h4" gutterBottom>
        Indicator Testing & Validation
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Test individual indicators with custom parameters and compare values with TradingView
      </Typography>

      <Grid container spacing={3}>
        {/* Configuration Panel */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Configuration</Typography>
            
            {/* Symbol Selection */}
            <Autocomplete
              options={availableSymbols}
              value={selectedSymbol}
              onChange={(event, newValue) => {
                setSelectedSymbol(newValue || '');
              }}
              freeSolo
              loading={symbolsLoading}
              filterOptions={(options, { inputValue }) => {
                return options.filter(option =>
                  option.toLowerCase().includes(inputValue.toLowerCase())
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Symbol"
                  margin="normal"
                  fullWidth
                  placeholder="Type to search symbols..."
                  helperText={symbolsLoading ? 'Loading symbols...' : `${availableSymbols.length} symbols available`}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {symbolsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const popularSymbols = ['RELIANCE', 'TCS', 'INFY', 'HDFCBANK', 'ICICIBANK', 'ITC', 'SBIN', 'LT', 'BHARTIARTL', 'KOTAKBANK'];
                const isPopular = popularSymbols.includes(option);
                
                return (
                  <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{option}</span>
                    {isPopular && (
                      <Chip label="Popular" size="small" color="primary" variant="outlined" sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }} />
                    )}
                  </Box>
                );
              }}
              sx={{ mt: 1, mb: 1 }}
            />

            {/* Timeframe and Period */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Timeframe</InputLabel>
                  <Select
                    value={timeframe}
                    onChange={(e) => setTimeframe(e.target.value)}
                    label="Timeframe"
                  >
                    <MenuItem value="1D">1 Day</MenuItem>
                    <MenuItem value="1H">1 Hour</MenuItem>
                    <MenuItem value="4H">4 Hour</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Days of Data"
                  type="number"
                  value={periodDays}
                  onChange={(e) => setPeriodDays(parseInt(e.target.value))}
                  fullWidth
                  margin="normal"
                  inputProps={{ min: 10, max: 365 }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Indicator Selection */}
            <Typography variant="subtitle1" gutterBottom>Select Indicator</Typography>
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                label="Category"
              >
                {Object.keys(supportedIndicators).map(category => (
                  <MenuItem key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedCategory && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Indicator</InputLabel>
                <Select
                  value={selectedIndicator}
                  onChange={(e) => handleIndicatorChange(e.target.value)}
                  label="Indicator"
                >
                  {Object.keys(supportedIndicators[selectedCategory] || {}).map(indicator => (
                    <MenuItem key={indicator} value={indicator}>{indicator}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Parameters */}
            {selectedIndicator && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Parameters</Typography>
                {renderParameterInputs()}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Action Buttons */}
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={testSingleIndicator}
                  disabled={loading || !selectedIndicator}
                  startIcon={<PlayArrowIcon />}
                >
                  Test Indicator
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={addToComparison}
                  disabled={!selectedIndicator}
                  size="small"
                >
                  Add to Compare
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={runComparison}
                  disabled={loading || comparisonIndicators.length === 0}
                  size="small"
                  startIcon={<CompareIcon />}
                >
                  Compare All
                </Button>
              </Grid>
            </Grid>

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
              </Box>
            )}
          </Paper>

          {/* Comparison List */}
          {comparisonIndicators.length > 0 && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Comparison List ({comparisonIndicators.length})
              </Typography>
              {comparisonIndicators.map((indicator, index) => (
                <Chip
                  key={index}
                  label={indicator.displayName}
                  onDelete={() => removeFromComparison(index)}
                  sx={{ m: 0.5 }}
                  size="small"
                />
              ))}
              <Button
                variant="text"
                size="small"
                onClick={() => setComparisonIndicators([])}
                sx={{ mt: 1, display: 'block' }}
              >
                Clear All
              </Button>
            </Paper>
          )}
        </Grid>

        {/* Results Panel */}
        <Grid item xs={12} md={8}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {renderResults()}

          {/* Indicator Documentation */}
          {selectedIndicator && supportedIndicators[selectedCategory] && supportedIndicators[selectedCategory][selectedIndicator] && (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  {supportedIndicators[selectedCategory][selectedIndicator].name}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  {supportedIndicators[selectedCategory][selectedIndicator].description}
                </Typography>
                {supportedIndicators[selectedCategory][selectedIndicator].parameters && 
                 supportedIndicators[selectedCategory][selectedIndicator].parameters.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Parameters:</Typography>
                    {supportedIndicators[selectedCategory][selectedIndicator].parameters.map(param => (
                      <Typography key={param.name} variant="body2" component="div">
                        • <strong>{param.name}</strong> ({param.type}): Default {param.default}
                        {param.min !== undefined && param.max !== undefined && 
                         ` (Range: ${param.min}-${param.max})`}
                      </Typography>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default IndicatorTesting;